import {
  Button,
  Icon,
  IconButton,
  Select,
  SelectOption,
  Spacer,
  Stack,
  Tag,
  TextBody,
  TextCaption,
  TextTitle,
  Tooltip,
} from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { useDeleteChannelModal } from '../components';
import {
  useChannelAnalyticsToggle,
  useLoadingState,
  useProStudioRoutes,
  useProStudioViewerRole,
} from '../hooks';
import {
  Channel,
  getChannelsFromPypes,
  MessageState,
  PlatformMessages,
  toAutorespondersInput,
  toPlatformMessages,
} from '../utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import { copyAppId, copyEmbedCode } from './channel-actions';
import { useCopySettingsModal } from './copy-channel-settings-modal';
import { DesignChannelConnections } from './design-channel-connections';
import { DesignChannelDetail } from './design-channel-detail';
import { MessageKey, PlatformSection } from './default-messages';

import './design-channel-panel.scss';
import { singleSelectValue } from '../utils/single-select-value';

export type ChannelKind = 'pype' | 'voice';

const KIND_TITLE: Record<ChannelKind, string> = {
  pype: 'Pype',
  voice: 'Voice',
};

/**
 * Default messages live on a channel's PYPE, per environment (the Default
 * Messages page's `defaultMessagesPypes[env]`, saved with
 * `updateDefaultMessages { id: pypeId, env }`) — so this edits one pype's
 * messages, however many channels share that pype.
 */
const PypeDefaultMessages: FC<{ pypeId: string; env: 'live' | 'sandbox' }> = ({
  pypeId,
  env,
}) => {
  const defaultMessagesPypes = useProStudioCtxSelector(
    (ctx) => ctx.defaultMessagesPypes
  );
  const isPageLoading = useLoadingState();
  const hasLoaded = defaultMessagesPypes != null;

  // Same load-once gating as the Default Messages page.
  useEffect(() => {
    if (!isPageLoading && !hasLoaded) {
      sendProStudioEvent({ type: 'loadDefaultMessages' });
    }
  }, [isPageLoading, hasLoaded]);

  const pype = defaultMessagesPypes?.[env]?.find((p) => p.id === pypeId);
  const [draft, setDraft] = useState<PlatformMessages | null>(null);
  const [dirty, setDirty] = useState(false);
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    setDraft(pype ? toPlatformMessages(pype) : null);
    setDirty(false);
  }, [pype]);

  if (!hasLoaded) {
    return (
      <TextCaption variant="tertiary">Loading default messages…</TextCaption>
    );
  }
  if (!pype || !draft) {
    return (
      <TextCaption variant="tertiary">
        No default messages for this channel.
      </TextCaption>
    );
  }

  const onChange = (key: MessageKey, value: MessageState) => {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
    setDirty(true);
  };

  const save = () =>
    sendProStudioEvent({
      type: 'updateDefaultMessages',
      payload: {
        id: pypeId,
        env,
        autoresponders: toAutorespondersInput(draft),
        cb: () => setDirty(false),
      },
    });

  return (
    <div data-testid="bobcat-design-channel-default-messages">
      <Select
        test-id="bobcat-design-channel-default-messages-language"
        variant="outlined"
        size="small"
        value={language}
        onChange={(e) => setLanguage(singleSelectValue(e.target.value))}
      >
        <SelectOption value="en">English - American (en)</SelectOption>
      </Select>
      <Spacer size="small" />
      <PlatformSection
        pype={pype}
        messages={draft}
        onMessageChange={onChange}
      />
      {dirty && (
        <>
          <Spacer size="small" />
          <Stack justifyContent="end" gutter="xsmall">
            <Button
              variant="secondary"
              size="small"
              onClick={() => {
                setDraft(toPlatformMessages(pype));
                setDirty(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              test-id="bobcat-design-channel-default-messages-save"
              onClick={save}
            >
              Save messages
            </Button>
          </Stack>
        </>
      )}
    </div>
  );
};

const ChannelThumb: FC<{ channel: Channel }> = ({ channel }) =>
  channel.type === 'twilio_voice' ? (
    <span className="c-design-channel-panel__thumb c-design-channel-panel__thumb--mark">
      <Icon name="call" size="small" />
    </span>
  ) : (
    <span className="c-design-channel-panel__thumb" aria-hidden="true">
      <span
        className="c-design-channel-panel__thumb-head"
        style={{
          background: channel.style?.widgetStyleColor || '#8573E7',
        }}
      />
      <span
        className="c-design-channel-panel__thumb-bubble c-design-channel-panel__thumb-bubble--agent"
        style={{
          background: channel.style?.agentBubbleColor || '#FFEDB4',
          color: channel.style?.agentBubbleTextColor || '#000',
        }}
      >
        Agent
      </span>
      <span
        className="c-design-channel-panel__thumb-bubble c-design-channel-panel__thumb-bubble--user"
        style={{
          background: channel.style?.userBubbleColor || '#736D9D',
          color: channel.style?.userBubbleTextColor || '#fff',
        }}
      >
        User
      </span>
    </span>
  );

/**
 * The Design stage's channel panel — the prototype's `renderChannelListPanel`
 * (`prostudio-build-flow-layouts.html`): every channel of one type, with the
 * Channels page's own actions, plus each pype's default messages. Replaces the
 * standalone Channels and Default Messages pages with the 5-stage nav on.
 */
/**
 * Analytics event logging for one channel — the same control as the Channels
 * page's Analytics column. Clicking it connects or disconnects the channel.
 */
const ChannelAnalyticsTag: FC<{
  channel: Channel;
  analytics: ReturnType<typeof useChannelAnalyticsToggle>;
}> = ({ channel, analytics }) => {
  const connected = analytics.isConnected(channel);
  const pending = analytics.isPending(channel);
  const interactive = analytics.isInteractive(channel);
  const tip = pending
    ? 'Updating Analytics…'
    : !interactive
      ? connected
        ? 'Events from this channel are logged in Analytics'
        : 'Events from this channel are not logged in Analytics'
      : connected
        ? 'Logged in Analytics'
        : 'Not logged in Analytics';
  return (
    <Tooltip content={tip}>
      <Tag
        size="small"
        variant={connected ? 'success' : 'error'}
        interactive={interactive}
        test-id="bobcat-design-channel-analytics"
        data-connected={connected ? 'true' : 'false'}
        onclick={(e: MouseEvent) => {
          e.stopPropagation();
          analytics.toggle(channel);
        }}
      >
        <Stack gutter="2xsmall" alignItems="center" nowrap>
          <Icon name={connected ? 'link' : 'unlink'} size="xsmall" />
          <TextCaption>
            {pending
              ? 'Analytics…'
              : connected
                ? 'Analytics on'
                : 'Analytics off'}
          </TextCaption>
        </Stack>
      </Tag>
    </Tooltip>
  );
};

export const DesignChannelPanel: FC<{
  kind: ChannelKind;
  channels: Channel[];
  onClose: () => void;
}> = ({ kind, channels, onClose }) => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const routes = useProStudioRoutes();
  const isViewer = useProStudioViewerRole();
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [openMessagesPype, setOpenMessagesPype] = useState<string | null>(null);
  const [openConnectionsChannel, setOpenConnectionsChannel] = useState<
    string | null
  >(null);
  // The prototype's channel detail panel: one channel's settings, in place,
  // instead of leaving Design for the channel settings page.
  const [detailChannelId, setDetailChannelId] = useState<string | null>(null);
  const detailChannel = channels.find((c) => c.id === detailChannelId) ?? null;

  const { deleteChannelModal, openModal: openDeleteChannelModal } =
    useDeleteChannelModal({ channel: selectedChannel });

  // Copy targets span every channel of the solution, not just this type.
  const botPypes = useProStudioCtxSelector((ctx) => ctx.botPypes);
  const allChannels = useMemo(
    () => getChannelsFromPypes(botPypes ?? []),
    [botPypes]
  );
  const analytics = useChannelAnalyticsToggle(allChannels);
  const [copySource, setCopySource] = useState<Channel | null>(null);
  const [copyRequest, setCopyRequest] = useState(0);
  const { copySettingsModal, openModal: openCopySettingsModal } =
    useCopySettingsModal({ source: copySource, channels: allChannels });
  // The modal only mounts once it has a source, so opening it in the same
  // click that sets the source would find no modal to open.
  useEffect(() => {
    if (copyRequest) openCopySettingsModal();
  }, [copyRequest, openCopySettingsModal]);

  // Messages belong to the pype, so a pype shared by several channels gets
  // one editor, under its first channel.
  const firstChannelOfPype = useMemo(() => {
    const seen = new Map<string, string>();
    channels.forEach((c) => {
      if (c.pypeId && !seen.has(c.pypeId)) seen.set(c.pypeId, c.id);
    });
    return seen;
  }, [channels]);
  const channelsPerPype = useMemo(() => {
    const counts = new Map<string, number>();
    channels.forEach((c) =>
      counts.set(c.pypeId, (counts.get(c.pypeId) ?? 0) + 1)
    );
    return counts;
  }, [channels]);

  const addChannelPath =
    kind === 'voice'
      ? routes.voice_channel_creation.path
      : routes.channel_creation.path;

  return (
    <aside
      className="c-design-channel-panel"
      aria-label={`${KIND_TITLE[kind]} channels`}
      data-testid="bobcat-design-channel-panel"
    >
      {/* An open channel brings its own header, as the prototype's panel. */}
      {!detailChannel && (
        <div className="c-design-channel-panel__head">
          <div>
            <TextTitle size="xsmall">{KIND_TITLE[kind]}</TextTitle>
            <TextCaption variant="secondary">
              {channels.length} channel{channels.length === 1 ? '' : 's'} on
              this type
            </TextCaption>
          </div>
          <IconButton
            name="close"
            size="small"
            aria-label="Close"
            test-id="bobcat-design-channel-panel-close"
            onClick={onClose}
          />
        </div>
      )}

      {detailChannel ? (
        <DesignChannelDetail
          channel={detailChannel}
          onBack={() => setDetailChannelId(null)}
          onClose={onClose}
        />
      ) : (
        <div className="c-design-channel-panel__list">
          {channels.length === 0 && (
            <TextBody size="small" variant="tertiary" textAlign="center">
              No channels on this type yet.
            </TextBody>
          )}
          {channels.map((channel) => {
            const isVoice = channel.type === 'twilio_voice';
            const ownsMessages =
              firstChannelOfPype.get(channel.pypeId) === channel.id;
            const sharedCount = channelsPerPype.get(channel.pypeId) ?? 1;
            const messagesOpen = openMessagesPype === channel.pypeId;
            // Voice channels don't carry these bindings in Channel Settings
            // either, so the panel matches it.
            const connectionsOpen =
              !isVoice && openConnectionsChannel === channel.id;

            return (
              <div
                key={channel.id}
                className="c-design-channel-panel__item"
                data-testid="bobcat-design-channel-row"
              >
                <div className="c-design-channel-panel__row">
                  <ChannelThumb channel={channel} />
                  <div className="c-design-channel-panel__row-head">
                    <TextBody size="small">{channel.name}</TextBody>
                    <div className="c-design-channel-panel__row-tags">
                      {channel.env && (
                        <Tag
                          size="small"
                          variant={
                            channel.env === 'live' ? 'success' : 'warning'
                          }
                        >
                          {channel.env === 'live' ? 'Live' : 'Sandbox'}
                        </Tag>
                      )}
                      {analytics.hasAnalytics && (
                        <ChannelAnalyticsTag
                          channel={channel}
                          analytics={analytics}
                        />
                      )}
                    </div>
                  </div>
                  <div className="c-design-channel-panel__row-actions">
                    <Button
                      size="small"
                      test-id="bobcat-design-channel-configure"
                      onClick={() => setDetailChannelId(channel.id)}
                    >
                      Customize
                    </Button>
                  </div>
                </div>

                <div className="c-design-channel-panel__row-tools">
                  {!isVoice && (
                    <>
                      <Tooltip content="Copy app ID">
                        <IconButton
                          name="copy"
                          size="small"
                          onClick={() => copyAppId(channel, t)}
                        />
                      </Tooltip>
                      <Tooltip content="Copy embed code">
                        <IconButton
                          name="tag"
                          size="small"
                          onClick={() => copyEmbedCode(channel, t)}
                        />
                      </Tooltip>
                    </>
                  )}
                  {!isViewer && allChannels.length > 1 && (
                    <Tooltip content="Copy settings to another channel">
                      <IconButton
                        name="duplicate"
                        size="small"
                        test-id="bobcat-design-channel-copy-settings"
                        onClick={() => {
                          setCopySource(channel);
                          setCopyRequest((n) => n + 1);
                        }}
                      />
                    </Tooltip>
                  )}
                  {!isViewer && (
                    <Tooltip content="Delete channel">
                      <IconButton
                        name="delete"
                        size="small"
                        test-id="bobcat-design-channel-delete"
                        onClick={() => {
                          setSelectedChannel(channel);
                          openDeleteChannelModal();
                        }}
                      />
                    </Tooltip>
                  )}
                  <span className="c-design-channel-panel__toggles">
                    {!isVoice && (
                      <button
                        type="button"
                        className="c-design-channel-panel__messages-toggle"
                        data-testid="bobcat-design-channel-connections-toggle"
                        aria-expanded={connectionsOpen}
                        onClick={() =>
                          setOpenConnectionsChannel(
                            connectionsOpen ? null : channel.id
                          )
                        }
                      >
                        Connections
                        <Icon
                          name={connectionsOpen ? 'chevron-up' : 'chevron-down'}
                          size="xsmall"
                        />
                      </button>
                    )}
                    {ownsMessages && (
                      <button
                        type="button"
                        className="c-design-channel-panel__messages-toggle"
                        data-testid="bobcat-design-channel-messages-toggle"
                        aria-expanded={messagesOpen}
                        onClick={() =>
                          setOpenMessagesPype(
                            messagesOpen ? null : channel.pypeId
                          )
                        }
                      >
                        Default messages
                        <Icon
                          name={messagesOpen ? 'chevron-up' : 'chevron-down'}
                          size="xsmall"
                        />
                      </button>
                    )}
                  </span>
                </div>

                {connectionsOpen && (
                  <div className="c-design-channel-panel__messages">
                    <DesignChannelConnections channel={channel} />
                  </div>
                )}

                {ownsMessages && messagesOpen && (
                  <div className="c-design-channel-panel__messages">
                    {sharedCount > 1 && (
                      <TextCaption variant="tertiary">
                        Shared by the {sharedCount} channels on this pype.
                      </TextCaption>
                    )}
                    <PypeDefaultMessages
                      pypeId={channel.pypeId}
                      env={channel.env}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isViewer && !detailChannel && (
        <button
          type="button"
          className="c-design-channel-panel__add"
          data-testid="bobcat-design-channel-add"
          onClick={() => navigate(addChannelPath)}
        >
          + Add channel
        </button>
      )}
      {deleteChannelModal}
      {copySettingsModal}
    </aside>
  );
};
